import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { AssignmentReturned, Close } from '@material-ui/icons';
import { ConfirmDialog } from '@sierralabs/react-material-ui';

import { ApiParams, FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import AssignDesignInputDialog from '../../products/dialogs/AssignDesignInputDialog';
import AssignUserNeedDialog from '../../products/dialogs/AssignUserNeedDialog';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from '../../products/Product.config';
import {
  ProductDesignInput,
  ProductDesignOutput,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../../products/Product.interface';
import HazardAssignReferenceDialog from '../dialogs/HazardAssignReferenceDialog';
import {
  useAssignHazardReferences,
  useFindHazardReferences
} from '../Hazard.hooks';
import AssignDesignOutputDialog from '../../products/dialogs/AssignDesignOutputDialog';
import AssignVerificationDialog from '../../products/dialogs/AssignVerificationDialog';
import AssignValidationDialog from '../../products/dialogs/AssignValidationDialog';
import {
  HazardReferenceTarget,
  HazardReferenceSource
} from '../Hazard.interface';
import { Task } from '../../tasks/Task.interface';
import { useTaskRedirect } from '../../tasks/Task.hooks';

export interface HazardReferenceCardProps {
  hazardId: number;
  productId: number;
  sourceEntityId?: number;
  version?: number;
  source?: HazardReferenceSource;
  target: HazardReferenceTarget;
  disabled?: boolean;
}

const HazardReferenceCard: React.FC<HazardReferenceCardProps> = ({
  hazardId,
  productId,
  sourceEntityId,
  version,
  source,
  target,
  disabled
}) => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const { result, redirect, getTaskEntity } = useTaskRedirect(selectedTask);
  const [order, setOrder] = useState<string>();
  const [orderDirection, setOrderDirection] = useState<'ASC' | 'DESC'>();
  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };
  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };
  //handle unassigning references
  const {
    updatedSource,
    isAssigningReferences,
    assigningReferenceError,
    assignReferences
  } = useAssignHazardReferences(hazardId, source, sourceEntityId, target);

  // Load or refreshing existing references
  const {
    references,
    isLoadingReferences,
    loadingReferenceError,
    findReferences
  } = useFindHazardReferences(hazardId, source, sourceEntityId, target);
  const reference = references?.[0];
  const [unassign, setUnassign] = useState<typeof reference>();
  const isLoading: Boolean =
    result.isLoading || isLoadingReferences || isAssigningReferences;
  const isError: FetchError | undefined =
    result.error || loadingReferenceError || assigningReferenceError;

  useEffect(() => {
    const params: ApiParams = {};
    if (order) params.order = order;
    if (orderDirection) params.direction = orderDirection;
    if (version) params.version = version;
    findReferences(params);
  }, [order, orderDirection, version, updatedSource, findReferences]);

  useEffect(() => {
    selectedTask && getTaskEntity();
  }, [selectedTask, getTaskEntity]);

  useEffect(() => {
    redirect && history.push(redirect);
  }, [redirect, history]);

  let displayName = '',
    queryAlias = '',
    idPrefix = 'ID';
  switch (target) {
    case 'causes':
      displayName = 'Causes';
      queryAlias = 'hazardCause';
      idPrefix = 'CAUSE';
      break;
    case 'controls':
      displayName = 'Control';
      queryAlias = 'hazardControl';
      idPrefix = 'CONTROL';
      break;
    case 'actions':
      displayName = 'Action';
      queryAlias = 'hazardAction';
      idPrefix = 'ACTION';
      break;
    case 'user-needs':
      displayName = getProductEntityName('userNeed');
      queryAlias = 'userNeed';
      idPrefix = getProductEntityIdPrefix('userNeed');
      break;
    case 'design-inputs':
      displayName = getProductEntityName('designInput');
      queryAlias = 'designInput';
      idPrefix = getProductEntityIdPrefix('designInput');
      break;
    case 'design-outputs':
      displayName = getProductEntityName('designOutput');
      queryAlias = 'designOutput';
      idPrefix = getProductEntityIdPrefix('designOutput');
      break;
    case 'verifications':
      displayName = getProductEntityName('verification');
      queryAlias = 'verification';
      idPrefix = getProductEntityIdPrefix('verification');
      break;
    case 'validations':
      displayName = getProductEntityName('validation');
      queryAlias = 'validation';
      idPrefix = getProductEntityIdPrefix('validation');
      break;
  }
  return (
    <Card>
      <CardTitle>
        <AssignmentReturned />
        {displayName} Reference
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={24}>
                <TableSortLabel
                  active={order === `${queryAlias}.id`}
                  direction={getSortDirection(`${queryAlias}.id`)}
                  onClick={() => handleSort(`${queryAlias}.id`)}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === `${queryAlias}.title`}
                  direction={getSortDirection(`${queryAlias}.title`)}
                  onClick={() => handleSort(`${queryAlias}.title`)}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {references?.map(reference => (
              <TableRow
                key={reference.id}
                hover
                onClick={() => setSelectedTask(reference.task)}
              >
                <TableCell>{`${idPrefix}-${reference.id}`}</TableCell>
                <TableCell>{reference.title}</TableCell>
                <TableCell>
                  <TaskStatusLabel task={reference.task} />
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <IconButton
                    size='small'
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      setUnassign(reference);
                    }}
                  >
                    <Close />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={disabled}
                  onClick={() => setOpenDialog(true)}
                >
                  Assign {displayName}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog &&
        references &&
        (source &&
        (target === 'controls' ||
          target === 'actions' ||
          target === 'causes') ? (
          <HazardAssignReferenceDialog
            hazardId={hazardId}
            target={target}
            displayName={displayName}
            queryAlias={queryAlias}
            idPrefix={idPrefix}
            references={references}
            onCancel={() => setOpenDialog(false)}
            onSubmit={targets => {
              setOpenDialog(false);
              assignReferences(targets.map(t => t.id));
            }}
          />
        ) : target === 'user-needs' ? (
          <AssignUserNeedDialog
            open
            productId={productId}
            selectedItems={references as ProductUserNeed[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={userNeeds => {
              setOpenDialog(false);
              assignReferences(userNeeds.map(un => un.id));
            }}
          />
        ) : target === 'design-inputs' ? (
          <AssignDesignInputDialog
            open
            productId={productId}
            selectedItems={references as ProductDesignInput[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={designInputs => {
              setOpenDialog(false);
              assignReferences(designInputs.map(di => di.id));
            }}
          />
        ) : target === 'design-outputs' ? (
          <AssignDesignOutputDialog
            open
            productId={productId}
            selectedItems={references as ProductDesignOutput[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={designOutputs => {
              setOpenDialog(false);
              assignReferences(
                designOutputs.map(designOutput => designOutput.id)
              );
            }}
          />
        ) : target === 'verifications' ? (
          <AssignVerificationDialog
            open
            productId={productId}
            selectedItems={references as ProductVerification[]}
            onCancel={() => setOpenDialog(false)}
            onSubmit={verifications => {
              setOpenDialog(false);
              assignReferences(
                verifications.map(verification => verification.id)
              );
            }}
          />
        ) : (
          target === 'validations' && (
            <AssignValidationDialog
              open
              productId={productId}
              selectedItems={references as ProductValidation[]}
              onCancel={() => setOpenDialog(false)}
              onSubmit={validations => {
                setOpenDialog(false);
                assignReferences(validations.map(validation => validation.id));
              }}
            />
          )
        ))}
      {unassign && (
        <ConfirmDialog
          open
          title='Are you sure?'
          message={`Are you sure you want to un-assign "${unassign.title}"?`}
          onCancel={() => setUnassign(undefined)}
          onConfirm={() => {
            if (!references) return;
            const updatedReferenceIds = references
              .map(r => r.id)
              .filter(id => id !== unassign.id);
            setUnassign(undefined);
            assignReferences(updatedReferenceIds);
          }}
        />
      )}
      {isError && (
        <AppSnackbar open severity='error'>
          {loadingReferenceError
            ? 'Error getting references. Please check your internet connection and try again.'
            : assigningReferenceError
            ? 'Error assigning reference'
            : isError.message}
        </AppSnackbar>
      )}
    </Card>
  );
};

export default HazardReferenceCard;
